import { useState, useEffect, SetStateAction } from "react";
import { Form, Input, Upload, Card, Typography, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadFile, UploadChangeParam, RcFile } from "antd/es/upload";
import type { FormInstance } from "antd/es/form";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../../firebase/firebase";

const { Title } = Typography;

const allowedFileTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "image/jpeg",
  "image/png",
];

const sizeLimit = 1024 * 1024 * 5;

const validateFile = (file: File) => {
  if (!allowedFileTypes.includes(file.type)) {
    message.error("Only PDF, Word, and image files are allowed.");
    return Upload.LIST_IGNORE;
  }
  if (file.size > sizeLimit) {
    message.error("File size exceeds the 5 MB limit.");
    return Upload.LIST_IGNORE;
  }
  return true;
};

const handleBeforeUpload = (file: RcFile) => {
  return validateFile(file) === true ? false : Upload.LIST_IGNORE;
};

const uploadFileToStorage = async (file: File, path: string) => {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

interface CVUploadProps {
  form: FormInstance;
  user: { uid: string; cv?: { file: string } };
  setLoadingCv: React.Dispatch<SetStateAction<boolean>>;
}

const CvUpload: React.FC<CVUploadProps> = ({ setLoadingCv, form, user }) => {
  const [cvFileList, setCvFileList] = useState<UploadFile<any>[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // Only set the initial CV value if the form doesn't already have a value.
    const currentCV = form.getFieldValue(["cv", "file"]);
    if (!currentCV && user?.cv && user.cv.file) {
      form.setFieldsValue({ cv: { file: user.cv.file } });
      setCvFileList([
        {
          uid: "-1",
          name: "current_cv",
          status: "done",
          url: user.cv.file,
        },
      ]);
    }
  }, [user, form]);

  const handleChange = async (info: UploadChangeParam) => {
    const newFileList = info.fileList.slice(-1);
    setCvFileList(newFileList);

    if (newFileList.length === 0) {
      form.setFieldsValue({ cv: { file: null } });
      return;
    }
    
    const file = newFileList[0].originFileObj;
    if (!file) {
      message.error("RcFile Error: File couldn't be uploaded");
      return;
    }

    try {
      setUploading(true);
      setLoadingCv(true);

      const cvUrl = await uploadFileToStorage(file, `enumerators/${user.uid}/cv/cv`);
      form.setFieldsValue({ cv: { file: cvUrl } });
      message.success("CV uploaded successfully!");
    } catch (error: any) {
      message.error("Error uploading CV: " + error.message);
      setCvFileList([]);
      form.setFieldsValue({ cv: { file: null } });
    } finally {
      setUploading(false);
      setLoadingCv(false);
    }
  };

  return (
    <Card className="px-8 py-2 mt-8">
      <Title level={4}>Upload Your CV</Title>
      <Form.Item name={['cv', 'file']} hidden>
        <Input />
      </Form.Item>
      <Upload
        listType="text"
        fileList={cvFileList}
        beforeUpload={handleBeforeUpload}
        onChange={handleChange}
        maxCount={1}
        disabled={uploading}
      >
        <button type="button" className="ant-btn width-100" disabled={uploading}>
          <UploadOutlined /> {uploading ? "Uploading..." : "Browse File"}
        </button>
      </Upload>
    </Card>
  );
};

export default CvUpload;
